/** @jsxImportSource @emotion/react */
import { useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import debounce from 'lodash/debounce'
import { useCallback, useMemo, useState } from 'react'
import { DEBOUNCE_DELAY, SEARCH_ITEMS_COUNT } from 'constants/consts'
import { findAllQuestionPassages, findAllQuestionPassagesVariables } from '__generatedTypes__/findAllQuestionPassages'
import { findOneQuestionPassageBank_findOneQuestionPassageBank_questionPassages as QuestionPassage } from '__generatedTypes__/findOneQuestionPassageBank'
import { QuestionPassageWhere, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import Autosuggest, { AutosuggestProps } from 'components/Autosuggest'
import Badge from 'components/Badge'
import { GET_QUESTION_PASSAGES_LIST } from 'queries/questionPassage'
import { colors } from 'theme/colors'
import { getLevelOption } from 'pagesNew/QuestionPassages/Form/consts'
import * as classes from 'components/QuestionPicker/styles'

export type QuestionPassagePickerProps = Omit<
  AutosuggestProps<QuestionPassage>,
  'items' | 'onInputChange' | 'loading' | 'changeImmediately' | 'multiple' | 'searchable'
> & {
  filter?: QuestionPassageWhere
}

const defaultRenderQuestion: AutosuggestProps<QuestionPassage>['renderItem'] = (questionPassage) => (
  <div css={classes.wrapper}>
    <Typography variant="body2" css={classes.questionTitle}>
      {questionPassage.title}
    </Typography>
    <div className="levels-wrapper" css={classes.badges}>
      {questionPassage.level.map((level) => (
        <Badge css={classes.badgeInner} key={level} text={getLevelOption(level).title} color={colors.blue} />
      ))}
    </div>
  </div>
)

export const QuestionPassagePicker = ({
  filter,
  renderItem = defaultRenderQuestion,
  placeholder = 'Search CARS Question',
  ...props
}: QuestionPassagePickerProps) => {
  const [search, setSearch] = useState('')

  const excludeIds = useMemo(() => {
    const { value } = props

    if (value) {
      return value?.map?.(({ id }) => id)
    }
  }, [props.value])

  const { data, loading } = useQuery<findAllQuestionPassages, findAllQuestionPassagesVariables>(
    GET_QUESTION_PASSAGES_LIST,
    {
      variables: {
        params: {
          take: SEARCH_ITEMS_COUNT,
          where: {
            excludeIds: excludeIds?.length ? excludeIds : undefined,
            ...filter,
          },
          search: search ? { query: search, type: SearchTypeEnum.ILike } : undefined,
        },
      },
      fetchPolicy: 'network-only',
    }
  )

  const handleQuestionPassageInputChange = useCallback(
    debounce((searchString: string) => {
      setSearch(searchString)
    }, DEBOUNCE_DELAY),
    []
  )

  const items = data?.findAllQuestionPassage.items ?? []

  return (
    <Autosuggest<QuestionPassage>
      css={classes.search}
      popperCss={classes.withPlatformsStyles}
      {...props}
      items={items}
      onInputChange={handleQuestionPassageInputChange}
      getItemLabel={(question) => question.title}
      renderItem={renderItem}
      placeholder={placeholder}
      loading={loading}
      changeImmediately
      multiple
      searchable
      data-testid="question-passages-picker"
    />
  )
}
